import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid2 as Grid,
  IconButton,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

type HeadingLevel = "h2" | "h3" | "h4" | "h5" | "h6";

export interface ViewAccordionField {
  label: string;
  value: React.ReactNode;
}

interface ViewAccordionProps<T> {
  items: T[];
  getItemId: (item: T, index: number) => string;
  getItemTitle: (item: T, index: number) => React.ReactNode;
  getItemFields: (item: T, index: number) => ViewAccordionField[];
  getItemSubtitle?: (item: T, index: number) => React.ReactNode;
  defaultExpandedIds?: string[];
  expandedIds?: string[];
  onExpandedIdsChange?: (ids: string[]) => void;
  allowMultiple?: boolean;
  unmountOnExit?: boolean;
  headingLevel?: HeadingLevel;
  pageSize?: number;
}

const ViewAccordion = <T,>({
  items,
  getItemId,
  getItemTitle,
  getItemFields,
  getItemSubtitle,
  defaultExpandedIds = [],
  expandedIds,
  onExpandedIdsChange,
  allowMultiple = false,
  unmountOnExit = true,
  headingLevel = "h4",
  pageSize = 10,
}: ViewAccordionProps<T>) => {
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize });
  const [internalExpandedIds, setInternalExpandedIds] =
    useState<string[]>(defaultExpandedIds);

  const totalPages = Math.max(1, Math.ceil(items.length / pagination.pageSize));
  const pageIndex = Math.min(pagination.pageIndex, totalPages - 1);
  const start = pageIndex * pagination.pageSize;
  const end = start + pagination.pageSize;
  const visibleItems = useMemo(() => items.slice(start, end), [items, start, end]);

  const currentExpandedIds = expandedIds ?? internalExpandedIds;

  const updateExpandedIds = (nextIds: string[]) => {
    if (onExpandedIdsChange) {
      onExpandedIdsChange(nextIds);
      return;
    }
    setInternalExpandedIds(nextIds);
  };

  const handleToggle = (itemId: string, isExpanded: boolean) => {
    if (allowMultiple) {
      const next = isExpanded
        ? Array.from(new Set([...currentExpandedIds, itemId]))
        : currentExpandedIds.filter((id) => id !== itemId);
      updateExpandedIds(next);
      return;
    }

    updateExpandedIds(isExpanded ? [itemId] : []);
  };

  const canPrev = pageIndex > 0;
  const canNext = pageIndex < totalPages - 1;

  const goPrev = () => {
    if (!canPrev) return;
    setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex - 1 }));
  };

  const goNext = () => {
    if (!canNext) return;
    setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex + 1 }));
  };

  if (items.length === 0) {
    return (
      <Grid size={{ xs: 12 }}>
        <Typography variant="body1">N/A</Typography>
      </Grid>
    );
  }

  return (
    <Grid size={{ xs: 12 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {visibleItems.map((item, localIndex) => {
          const globalIndex = start + localIndex;
          const itemId = getItemId(item, globalIndex);
          const expanded = currentExpandedIds.includes(itemId);
          const subtitle = getItemSubtitle?.(item, globalIndex);
          const fields = getItemFields(item, globalIndex);

          return (
            <Accordion
              key={itemId}
              expanded={expanded}
              onChange={(_event, nextExpanded) =>
                handleToggle(itemId, nextExpanded)
              }
              slotProps={{
                heading: { component: headingLevel },
                transition: { unmountOnExit },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box>
                  {subtitle ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", lineHeight: 1.2 }}
                    >
                      {subtitle}
                    </Typography>
                  ) : null}
                  <Typography variant="subtitle1">
                    {getItemTitle(item, globalIndex)}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {fields.length > 0 ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {fields.map((field, fieldIndex) => (
                      <Box
                        key={`${itemId}-${field.label}-${fieldIndex}`}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            width: 150,
                            minWidth: 150,
                            pt: 0.25,
                          }}
                        >
                          {field.label}
                        </Typography>
                        <Box sx={{ flex: 1, minWidth: 0 }}>{field.value}</Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    N/A
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1,
          mt: 1,
        }}
      >
        <IconButton onClick={goPrev} disabled={!canPrev} size="small">
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="caption" color="text.secondary">
          Page {pageIndex + 1} of {totalPages}
        </Typography>
        <IconButton onClick={goNext} disabled={!canNext} size="small">
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Grid>
  );
};

export default ViewAccordion;
